import CloseIcon from '@mui/icons-material/Close'
import { Paper, Button, IconButton, Box, Stack } from '@mui/material'
import { GLOBAL_PROFILE, useRootLoaderData } from 'components/Root.loader'
import DateSelector from 'components/Search/DateSelector'
import SelectField from 'components/Search/SelectField'
import Field from 'components/ui/Field'
import { DOC_TYPE, DocType, STATUS_CODE } from 'config'
import useMode from 'hooks/useMode'
import useOcrBuilders from 'hooks/useOcrBuilders'
import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { OcrBuilder } from 'types'

import InlineLabel from './InlineLabel'
import { SearchParams } from './Search'
import TextField from './TextField'

const allStatus = (inDeletePage: boolean) => {
  if (inDeletePage) {
    return ['削除']
  } else {
    return ['新規登録', '編集済み', '出力済み', '送信エラー']
  }
}

const withBlankOption = (label, options) => [{ value: '', label }].concat(options)
const withMinusOption = (label, options) => [{ value: -1, label }].concat(options)

const getSelectDocTypeNameOptions = (docTypes: DocType[], ocrBuilders: OcrBuilder[]) => {
  const docTypeNames = [...docTypes, ...ocrBuilders.map((x) => x.documentTypeName)]
  return withBlankOption(
    'すべてのタイプ',
    docTypeNames.map((docType) => ({ label: docType, value: docType })),
  )
}

interface Props {
  search: SearchParams
  onCloseClick: () => void
  onChange: any
  onReset: any
  usingCustomDate: boolean
  setUsingCustomDate: any
}

const Filters = (props: Props) => {
  const { search, onCloseClick, onReset, onChange, usingCustomDate, setUsingCustomDate } = props
  const { mode } = useMode()
  const location = useLocation()

  const rootData = useRootLoaderData()
  const { normalOcrBuilders, verifyOcrBuilders } = useOcrBuilders()
  const docTypes = useMemo(
    () =>
      GLOBAL_PROFILE.docTypes.filter(
        (x) => x !== DOC_TYPE.OCR_BUILDER && x !== (mode !== 'normal' ? DOC_TYPE.GENERAL_OCR : ''),
      ),
    [mode],
  )

  const docTypeNameOptions = getSelectDocTypeNameOptions(
    docTypes,
    mode === 'normal' ? normalOcrBuilders : verifyOcrBuilders,
  )

  const isDeleted = location.pathname.endsWith('/deleted')

  const nGroups = rootData.normalGroups
  const vGroups = rootData.verifyGroups
  const groupOptions = useMemo(
    () =>
      withBlankOption(
        'すべてのグループ',
        (mode === 'normal' ? nGroups : vGroups).map((group) => ({ value: group, label: group })),
      ),
    [mode, nGroups, vGroups],
  )

  useEffect(() => {
    // verify modeはsearchが無い
    // eslint-disable-next-line no-constant-condition
    if (false) {
      if ((mode === 'normal' ? nGroups : vGroups).findIndex((g) => g === search.subGroup) === -1)
        onChange({ target: { name: 'subGroup', value: '' } })
    }
  }, [mode, search, nGroups, vGroups, onChange])

  const statuses = allStatus(isDeleted)

  const { updateDateFrom, updateDateTo } = search

  const handleUpdateDateChange = (changedAttr) => {
    const {
      dateFrom = updateDateFrom,
      dateTo = updateDateTo,
      isCustom = usingCustomDate,
    } = changedAttr
    setUsingCustomDate(isCustom)
    onChange({ target: { name: 'updateDateFrom', value: dateFrom } })
    onChange({ target: { name: 'updateDateTo', value: dateTo } })
  }

  const statusOptions = withMinusOption(
    'すべてのステータス',
    statuses.map((status) => ({ label: status, value: STATUS_CODE[status] })),
  )

  return (
    <Paper>
      <Box textAlign="right">
        <IconButton onClick={onCloseClick}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Stack sx={{ pl: 2, pr: 7 }}>
        <SelectField
          name="docTypeName"
          label="文書タイプ"
          value={search.docTypeName}
          onChange={onChange}
          options={docTypeNameOptions}
          testid="doc-type-name"
        />

        <SelectField
          name="status"
          label="ステータス"
          value={search.status}
          onChange={onChange}
          options={statusOptions}
          testid="status"
        />

        <DateSelector
          name="updateDate"
          label="更新日時"
          onChange={handleUpdateDateChange}
          dateFrom={updateDateFrom}
          dateTo={updateDateTo}
          isCustom={usingCustomDate}
        />

        <Field inline label={<InlineLabel>更新者</InlineLabel>}>
          <TextField
            name="updateUserName"
            margin="dense"
            placeholder="更新者の名前を入力"
            value={search.updateUserName}
            onChange={onChange}
            data-testid="update-user-name"
          />
        </Field>
        <Field inline label={<InlineLabel>担当者</InlineLabel>}>
          <TextField
            name="assigneeName"
            margin="dense"
            placeholder="担当者の名前を入力"
            value={search.assigneeName}
            onChange={onChange}
            data-testid="assignee-name"
          />
        </Field>
        <SelectField
          name="subGroup"
          label="グループ"
          value={search.subGroup}
          options={groupOptions}
          onChange={onChange}
          testid="sub-group"
        />
      </Stack>
      <Box p={1} textAlign="right">
        <Button color="primary" onClick={onReset} data-testid="filter-reset-button">
          リセット
        </Button>
        <Button type="submit" variant="contained" color="primary" data-testid="search-button">
          検索
        </Button>
      </Box>
    </Paper>
  )
}

export default Filters
